import { Participant, ParticipantOption, ParticipantPriceStatus, Price } from '@cds/betting-offer';
import { FixturePricingState } from '@cds/betting-offer/add-ons';
import { DayOfRaceDate } from '@cds/betting-offer/domain-specific/horse-racing';
import {
    GreyhoundParticipantData as RacingPostGreyhoundParticipantData,
    HorseParticipantData as RacingPostHorseParticipantData,
} from '@cds/betting-offer/domain-specific/horse-racing/racing-post';
import { HorseParticipantData } from '@cds/betting-offer/domain-specific/horse-racing/timeform';
import { Meeting, TagCount } from '@cds/betting-offer/tags';
import { EventOption } from '@frontend/sports/betting-offer/feature/model';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { Nullable, StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';

import { FixtureParticipantPriceMap, V3FixtureParticipantPriceMap } from './fixture-participant-price-map';
import { ForecastComponentMode } from './forecast-mode';
import { HorseRaceSortingDirection } from './horse-race-sorting-direction';
import { MarketView } from './market-view';
import { RaceMarketViewModel } from './race-market-view-model';
import { WinGameFixture, WinGameFixturePreview } from './win-game-fixture';

export interface ForecastPlaces {
    first: WinGameParticipant | null;
    second: WinGameParticipant | null;
    third: WinGameParticipant | null;
    fourth: WinGameParticipant | null;
    fifth: WinGameParticipant | null;
}

export enum RacingConstants {
    Notavailable = 'N/A',
}

export class ForecastModel {
    first = false;
    second = false;
    third = false;
    fourth = false;
    fifth = false;
    anyForecast = false;
    anyTricast = false;

    checkboxId: {
        [x in keyof ForecastPlaces]: string;
    } & { anyForecast: string; anyTricast: string } = {
        first: '',
        second: '',
        third: '',
        fourth: '',
        fifth: '',
        anyForecast: '',
        anyTricast: '',
    };

    constructor(
        public participant: WinGameParticipant,
        public showForecastOption: boolean,
        public showTricastOption: boolean,
        mode: ForecastComponentMode,
    ) {
        this.checkboxId = {
            first: `cb-${mode}-${participant.id}-first`,
            second: `cb-${mode}-${participant.id}-second`,
            third: `cb-${mode}-${participant.id}-third`,
            fourth: `cb-${mode}-${participant.id}-fourth`,
            fifth: `cb-${mode}-${participant.id}-fifth`,
            anyForecast: `cb-${mode}-${participant.id}-any-forecast`,
            anyTricast: `cb-${mode}-${participant.id}-any-tricast`,
        };
    }
}

export interface RaceParticipantViewModel<TParticipantInfo> extends WinGameParticipant {
    attributes: TParticipantInfo;
    isSPOptionActive?: boolean;
    priceHistory: Price[];
    isOptionActive: boolean;
    availableBadges?: HorseBadge[];
    easyBetViewStats?: { [attributeName: string]: EasyBetViewStats };
    isSpotlightInformationAvailable?: boolean;
}

export interface RaceParticipantViewModelWithOptions<TParticipantInfo, Toption> extends RaceParticipantViewModel<TParticipantInfo> {
    option?: Toption;
    spOption?: Toption;
}

export interface EasyBetViewStats {
    color: string;
    label?: string;
    grade?: number;
}

export interface WinGameParticipant extends Participant {
    id: number;
    favouriteRank?: string;
    //TODO try to replace native price with Odds

    nativePrice?: NativePrice;
    timeformInfo?: HorseParticipantData;
    racingPostInfo?: RacingPostData;
}

export interface RacingHorseParticipantData extends RacingPostHorseParticipantData {
    hasTopStarRating: boolean;
}

export interface RacingPostData {
    horseData?: RacingHorseParticipantData;
    greyhoundsData?: RacingPostGreyhoundParticipantData;
}

export enum HorseRaceFavouritePrefixes {
    Favourite = 'Favourite',
    FavouriteCo = 'FavouriteCo',
    FavouriteJoint = 'FavouriteJoint',
}

export interface ForecastParticipantViewModel extends WinGameParticipant {
    availableBadges: HorseBadge[];
    participantInfo: RacingParticipantInfo;
    visible: boolean;
    marketStatus: boolean | undefined;
}

export interface RaceResultedPlacedParticipant extends WinGameParticipant {
    isFavourite: boolean;
    favouriteLabel: string;
    attributes: RacingParticipantInfo;
}

export enum HorseBadge {
    BeatenFavourite = 'beatenFavourite',
    CourseWins = 'courseWins',
    DistanceWins = 'distanceWins',
    CourseAndDistanceWins = 'courseAndDistanceWins',
}

export interface RacingParticipantInfo {
    jockey?: string;
    trainer?: string;
    isFavorite: boolean;
}

export interface HorseParticipantInfo {
    raceCardNumber?: number;
    drawNumber?: number;
    jockey: string;
    trainer: string;
    silkUrl: string;
    silkDescription: string;
    isFavorite: boolean;
    ratingStars?: number;
}

export interface GreyhoundParticipantInfo {
    trapNumber: number;
    greyhoundName: string;
    cloth: string;
}

export interface GreyhoundParticipantDictionary {
    [id: number]: GreyhoundParticipantInfo;
}

export enum RacingPopups {
    HrHelp = 'hr-help-overlay',
    HrInfo = 'hr-info-overlay',
    GhHelp = 'gr-help-overlay',
    GhInfo = 'gr-info-overlay',
}

export enum RacingPopupCSS {
    HrHelp = 'hr-help',
    HrInfo = 'hr-info',
    GhHelp = 'gh-help',
    GhInfo = 'gh-info',
}

export const RacingTrackingConstants = {
    RACING_POST: 'racing post',
    RACING_RATINGS_INFO: 'ratings info popup',
    RACING_CARD_PAGE: 'race card page',
    RACING_RATINGS_INFO_ICON: 'ratings info icon',
};

export interface RouteParams {
    sportId: SportConstant;
    activeTab: RacingUrlParam;
}

export enum RacingUrlParam {
    Today = 'today',
    Tomorrow = 'tomorrow',
    Future = 'future',
    TodaysResults = 'todays-results',
    NextRaces = 'nextRaces',
}

export interface MeetingModel {
    meeting: {
        id: number;
        name: string;
    };
    fixtures: WinGameFixturePreview[];
    day: DayOfRaceDate;
    allFixtures?: WinGameFixture[];
}

export type RacingPostParticipantBase = RaceParticipantViewModel<RacingParticipantInfo>;
export type TimeFormParticipantBase = RaceParticipantViewModel<HorseParticipantInfo>;

export type RacingParticipantWithParticipantOption = RaceParticipantViewModelWithOptions<RacingParticipantInfo, ParticipantOption>;
export type RacingParticipantWithEventOption = RaceParticipantViewModelWithOptions<RacingParticipantInfo, EventOption>;

export type TimeformParticipantWithParticipantOption = RaceParticipantViewModelWithOptions<HorseParticipantInfo, ParticipantOption>;

export type TimeformParticipantWithEventOption = RaceParticipantViewModelWithOptions<HorseParticipantInfo, EventOption>;

export interface HorsesRaceWinMarketComponentViewModel {
    sortBy: HorseRaceSortingDirection;
    runners: (RacingParticipantWithParticipantOption | RacingParticipantWithEventOption)[];
    nonRunners: (RacingParticipantWithParticipantOption | RacingParticipantWithEventOption)[];
    allPricesLocked: boolean;
    allPricesSP: boolean;
}

export interface HorsesParticipantComponentViewModel {
    showAge: boolean;
    showWeight: boolean;
    showBadge: boolean;
    showHorseInfo: boolean;
    showSummaryPredictor: boolean;
    showEasyBetViewStats: boolean;
}

export interface AntepostParticipantsViewModel<TParticipantPriceMap extends FixtureParticipantPriceMap | V3FixtureParticipantPriceMap> {
    fixture?: WinGameFixture;
    participants: WinGameParticipant[];
    options: TParticipantPriceMap;
}

export interface RaceDetailsModel extends MeetingModel {
    fixture: WinGameFixture;
}

export interface WinningMarketDetails {
    winningMarket: string;
    showParticipantImage: boolean;
}

export interface HorsesRaceWinMarketBindings {
    fixture: WinGameFixture;
    displaySorting: boolean;
    displayRunners: boolean;
}

export interface HorsesForecastBindings {
    mode: ForecastComponentMode;
    fixture: WinGameFixture;
}

export interface AntepostMarketComponentBindings {
    fixture: WinGameFixture;
}

export interface HorsesMeetingAllRaceViewModel {
    fixture: WinGameFixturePreview;
    pricingState: FixturePricingState;
    fixtureUrl: string;
    startDate: string;
    raceName: string;
}

export interface HorsesRaceResultedViewModel {
    placed: WinGameParticipant[];
    nonRunners: WinGameParticipant[];
    dividends: { name: string; value: string; available: boolean; result: string }[];
    hasDividends: boolean;
    hasSilks: boolean;
    favorites: WinGameParticipant[];
    participantOptionGroups: StringDictionary<EventOption[]>;
}

export interface GreyhoundsRaceWinMarketComponentViewModel {
    sortBy: HorseRaceSortingDirection;
    runners: (RacingParticipantWithParticipantOption | RacingParticipantWithEventOption)[];
    nonRunners: string | (RacingParticipantWithParticipantOption | RacingParticipantWithEventOption)[];
    allPricesLocked: boolean;
    allPricesSP: boolean;
    expandedParticipant: { [id: number]: boolean };
}

export interface ForecastColumn {
    id: keyof ForecastPlaces;
    name: string;
    visible: boolean;
}

export type TournamentGroup =
    | {
          id: DayOfRaceDate;
          expanded: boolean;
          tournaments: {
              meeting: TagCount;
              attributes?: {
                  selected: boolean;
              };
          }[];
      }
    | {
          id: 'Future';
          expanded: boolean;
          tournaments: {
              url: string;
              meeting: Meeting;
          }[];
      };

export interface HorsesRaceComponentViewModel {
    selectedMarketView: Nullable<MarketView>;
    prices: FixtureParticipantPriceMap;
    timeformTipRunner?: WinGameParticipant;
    marketViews: RaceMarketViewModel[];
    priceStatusVisible: ParticipantPriceStatus;
    winMarketSuspended: boolean;
    canSelectTopForecast: boolean;
    canSelectTopTricast: boolean;
}

export enum DerivedMarkets {
    Racewinner = 'Race Winner',
    Forecast = 'ForeCast',
    Tricast = 'TriCast',
    CombinationForecast = 'CombinationForecast',
    CombinationtTricast = 'CombinationtTricast',
}
